/**
 * Card de dependente do Step 3.
 *
 * Duas responsabilidades: manter ou remover o dependente da extensão (checkbox)
 * e corrigir os dados cadastrais (edição inline). A edição abre no próprio card,
 * com rascunho local — só o "Salvar" promove as mudanças para o estado do
 * wizard, então "Cancelar" descarta sem efeito colateral.
 *
 * ATENÇÃO: as correções não têm campo no `ISubmitPayload` (ver a pendência de
 * contrato em `types.ts`). Hoje elas só alimentam a revisão do Step 5.
 */

import { Switch, TextField } from "@mui/material";
import { useState } from "react";
import styled from "styled-components";

import { cpfMask, cpfInputMask } from "utils/cpf";

import {
  formatDependentSummary,
  formatIsoDate,
  getInitials,
} from "../helpers";
import {
  Button,
  Caption,
  Panel,
  Row,
  SectionLabel,
  Stack,
  TwoColumns,
} from "../styled";
import { COLORS } from "../tokens";
import { IDependent, IDependentEdit } from "../types";

const CPF_DIGITS = 11;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 999px;
  background-color: ${COLORS.brandSoft};
  color: ${COLORS.brandInk};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
`;

/** `min-width: 0` permite o nome longo ser cortado em vez de empurrar o checkbox. */
const Identity = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Name = styled(SectionLabel)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemovedNote = styled(Caption)`
  color: ${COLORS.inkDisabled};
`;

interface IProps {
  dependent: IDependent;
  isRetained: boolean;
  /** Correção já salva para este dependente, se houver. */
  edit?: IDependentEdit;
  onToggleRetained: () => void;
  onSaveEdit: (edit: IDependentEdit) => void;
}

/** Dados exibidos: a correção salva prevalece sobre o que veio no `resolve`. */
function buildDraft(
  dependent: IDependent,
  edit?: IDependentEdit
): IDependentEdit {
  return {
    name: edit?.name ?? dependent.name,
    relationship: edit?.relationship ?? dependent.relationship,
    taxPayerId: edit?.taxPayerId ?? dependent.taxPayerId,
    birthDate: edit?.birthDate ?? dependent.birthDate ?? "",
  };
}

export function DependentCard({
  dependent,
  isRetained,
  edit,
  onToggleRetained,
  onSaveEdit,
}: IProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<IDependentEdit>(() =>
    buildDraft(dependent, edit)
  );

  const current = buildDraft(dependent, edit);
  const canSave = draft.name.trim().length > 0;

  function startEditing() {
    setDraft(buildDraft(dependent, edit));
    setIsEditing(true);
  }

  function save() {
    onSaveEdit({
      name: draft.name.trim(),
      relationship: draft.relationship.trim(),
      taxPayerId: draft.taxPayerId,
      birthDate: draft.birthDate,
    });
    setIsEditing(false);
  }

  return (
    <Panel $selected={isRetained}>
      <Stack $gap="16px">
        <Row
          $gap="12px"
          $justify="space-between"
        >
          <Row $gap="12px">
            <Avatar>{getInitials(current.name)}</Avatar>
            <Identity>
              <Name>{current.name}</Name>
              <Caption>
                {formatDependentSummary(current.relationship, dependent.age)} ·{" "}
                {cpfMask(current.taxPayerId)}
              </Caption>
            </Identity>
          </Row>

          <Switch
            checked={isRetained}
            onChange={onToggleRetained}
            inputProps={{ "aria-label": `Manter ${current.name} no plano` }}
          />
        </Row>

        {!isRetained && (
          <RemovedNote>Não será incluído na extensão do plano.</RemovedNote>
        )}

        {isRetained && !isEditing && (
          <Row $justify="flex-end">
            <Button
              variant="text"
              onClick={startEditing}
            >
              Verificar dados
            </Button>
          </Row>
        )}

        {isEditing && (
          <Stack $gap="16px">
            <TwoColumns>
              <TextField
                label="Nome"
                value={draft.name}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
                error={!canSave}
                helperText={!canSave ? "Informe o nome." : undefined}
              />
              <TextField
                label="Parentesco"
                value={draft.relationship}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    relationship: event.target.value,
                  }))
                }
              />
            </TwoColumns>

            <TwoColumns>
              <TextField
                label="CPF"
                value={cpfInputMask(draft.taxPayerId)}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    taxPayerId: event.target.value
                      .replace(/\D/g, "")
                      .substring(0, CPF_DIGITS),
                  }))
                }
              />
              <TextField
                label="Data de nascimento"
                type="date"
                value={draft.birthDate}
                onChange={(event) =>
                  setDraft((previous) => ({
                    ...previous,
                    birthDate: event.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                helperText={
                  draft.birthDate ? formatIsoDate(draft.birthDate) : undefined
                }
              />
            </TwoColumns>

            <Row
              $gap="8px"
              $justify="flex-end"
            >
              <Button
                variant="outlined"
                onClick={() => setIsEditing(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={save}
                disabled={!canSave}
              >
                Salvar
              </Button>
            </Row>
          </Stack>
        )}
      </Stack>
    </Panel>
  );
}
